import React, { useEffect, useRef } from 'react';

import { DiskButton } from 'apps/auth/components/common/DiskButton/DiskButton';
import PopupPortal from 'apps/auth/components/common/Popup/PopupPortal';
import { USER_THEME_DARK } from 'apps/auth/constants/Constants';
import { useThemeDetector } from 'apps/auth/hooks/theme';
import clsx from 'clsx';

import styles from './BrazeIamSlideup.module.css';

const SWIPE_CLOSE_THRESHOLD_PX = 40;
const AUTO_CLOSE_MS = 5000;

const BrazeIamSlideup = ({ config, onBodyClick, onClose }) => {
    const { theme } = useThemeDetector();
    const palette = theme === USER_THEME_DARK ? config.colors.dark : config.colors.light;
    const clickable = Boolean(config.webUrl);
    const isTop = config.animation === 'top';
    const touchStartYRef = useRef(null);

    useEffect(() => {
        const timerId = setTimeout(onClose, AUTO_CLOSE_MS);
        return () => clearTimeout(timerId);
    }, [onClose]);

    // Swipe to dismiss (mobile web): swipe away from where it entered (down for bottom, up for top).
    const handleTouchStart = (e) => {
        touchStartYRef.current = e.touches[0]?.clientY ?? null;
    };
    const handleTouchEnd = (e) => {
        if (touchStartYRef.current === null) {
            return;
        }
        const deltaY = (e.changedTouches[0]?.clientY ?? 0) - touchStartYRef.current;
        const dismissed = isTop ? deltaY < -SWIPE_CLOSE_THRESHOLD_PX : deltaY > SWIPE_CLOSE_THRESHOLD_PX;
        touchStartYRef.current = null;
        if (dismissed) {
            onClose();
        }
    };

    return (
        <PopupPortal>
            <div
                className={clsx(styles.wrapper, isTop ? styles.wrapperTop : styles.wrapperBottom)}
                role="dialog"
                aria-live="polite"
                aria-label={config.title || config.subtitle}
            >
                <div
                    className={clsx(styles.card, isTop ? styles.cardTop : styles.cardBottom, clickable && styles.clickable)}
                    style={{ background: palette.background, color: palette.textColor }}
                    onClick={clickable ? onBodyClick : undefined}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    data-testid="braze-iam-slideup"
                >
                    {config.promoImage && <img className={styles.promoImage} src={config.promoImage} alt="" aria-hidden="true" />}
                    <div className={styles.textBlock}>
                        {config.title && <div className={styles.title}>{config.title}</div>}
                        {config.subtitle && <div className={styles.subtitle}>{config.subtitle}</div>}
                    </div>
                    {config.closeButton && (
                        <span className={styles.bannerCloseButton}>
                            <DiskButton
                                size="small"
                                iconName="cross"
                                iconColor="white_base"
                                type="button"
                                variant="glass"
                                labelText="Close"
                                dataTestId="braze-iam-slideup-close"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onClose();
                                }}
                            />
                        </span>
                    )}
                </div>
            </div>
        </PopupPortal>
    );
};

export default BrazeIamSlideup;
